import dayjs from "dayjs";
import Big from "big.js";
import { formatToCompanyTimezone, formatToUtc, getDateTimeFromSelectedDate, getYearFromCompanyDate } from "../lib/functions";

// Discount for a single item row: reads whichever mode is active
// (percent, the default/undefined-safe mode, or a flat amount), clamped
// so a flat amount can never exceed the item's own line total.
const getItemDiscountAmount = (item, line) => {
  if (item?.discountMode === "amount") {
    const raw = new Big(item?.discountAmt || 0);
    return raw.gt(line) ? line : raw.lt(0) ? new Big(0) : raw;
  }
  return line.times(item?.discount || 0).div(100);
};

export const createServiceEntryPayload = (state, authInfo) => {
  const {
    customerInfo,
    itemCart,
    footerValues,
    narration,
    voucherNumber,
    service_id,
    entryDate,
    tagFieldId,
    conversion_rate,
    conversion_currency,
    selectedServiceAcc,
    selectedDiscountAcc,
    selectedTaxAcc,
    selectedTransportAcc,
    taxMode,
    payServiceCart,
    tag_field_id
  } = state;

  let finalEntryDate = entryDate;
  if (!dayjs(entryDate).isSame(dayjs(), "day")) {
    finalEntryDate = getDateTimeFromSelectedDate(entryDate); // Merge date + current time in TZ

  } else {
    finalEntryDate = entryDate; // Use now in TZ
  }

  let dateUTC = formatToUtc(finalEntryDate);
  let companydate = formatToCompanyTimezone(dateUTC);
  const vchYear = getYearFromCompanyDate(companydate);



  const payload = {
    service_id: service_id || 0,
    vchdate: vchYear,

    itemCart: itemCart.map((it) => {
      const quantity = Big(it.quantity || 0);
      const rate = Big(it.rate || 0);
      const taxPct = Big(it.tax_per || 0);
      const baseAmount = quantity.times(rate);

      const discountAmount = getItemDiscountAmount(it, baseAmount);
      const discounted = baseAmount.minus(discountAmount);
      const taxAmount = discounted.times(taxPct).div(100);
      const total = discounted.plus(taxAmount);

      // Back-computed so item_discount_per stays meaningful (e.g. for
      // printed vouchers) even when the row is in flat-amount mode.
      const discountPct = it.discountMode === "amount"
        ? (baseAmount.eq(0) ? 0 : Number(discountAmount.div(baseAmount).times(100).toFixed(13)))
        : Big(it.discount || 0);

      const isOnTotal = taxMode === "on_total";

      return {
        warehouse_id: it.warehouse_id || 0,
        warehouse_name: it.warehouse_name || "",
        item_id: it.item_id,
        item_name: it.product,
        item_description: it.item_description || "",
        item_qty: quantity.toString(),
        service_qty: quantity.toString(),
        item_rate: (rate).toString(),
        service_rate: rate.toString(),


        item_discount: isOnTotal ? 0 : discountAmount.toString(),
        item_discount_per: isOnTotal ? 0 : discountPct,
        discount_mode: it.discountMode === "amount" ? "amount" : "percent",
        discount_acc_id: selectedDiscountAcc?.acc_id || 0,
        discount_acc_name: selectedDiscountAcc?.acc_name || "",

        item_tax: isOnTotal ? 0 : taxAmount.toString(),
        item_tax_per: isOnTotal ? 0 : taxPct,
        tax_acc_id: selectedTaxAcc?.acc_id || 0,
        tax_acc_name: selectedTaxAcc?.acc_name || "",

        item_total: isOnTotal
          ? baseAmount.toString()
          : total.toString(),
        qty_display: `${quantity} ${it.unit_symbol || ""}`,
        unit_name: it.unit_name || "",
        unit_symbol: it.unit_symbol || "",
        base_unit_name: it.base_unit_name || null,
        per_unit_id: it.unit_id || 0, //
        per_unit_symbol: it.unit_symbol || "",
        conversion: Number(it.conversion) || 1,
        per_conversion: Number(it.conversion) || 1,
        retail_qty: Number(it.retail_qty) || 0,
        is_serial: it.is_serial || "no",
        serials: it.serials || [],
        units: it.units || [],
      };
    }),


    // itemCart: itemCart.map((it) => ({
    //   warehouse_id: it.warehouse_id || 0,
    //   warehouse_name: it.warehouse_name || "",
    //   item_id: it.item_id,
    //   item_name: it.product,
    //   item_qty: String(it.quantity),
    //   service_qty: it.quantity,
    //   item_rate: String(it.rate),
    //   service_rate: it.rate,
    //   item_discount: 0,
    //   item_discount_per: it.discount || 0,
    //   discount_acc_id: selectedDiscountAcc?.acc_id || 0,
    //   discount_acc_name: selectedDiscountAcc?.acc_name || "",
    //   item_tax: (
    //     ((Number(it.quantity) * Number(it.rate)) -
    //       ((it.discount || 0) / 100) * (Number(it.quantity) * Number(it.rate))) *
    //     (Number(it.tax_per) || 0)
    //   ) / 100,
    //   item_tax_per: it.tax_per || 0,
    //   item_total: (
    //     Number(it.quantity) * Number(it.rate) +
    //     (((Number(it.quantity) * Number(it.rate)) -
    //       ((it.discount || 0) / 100) * (Number(it.quantity) * Number(it.rate))) *
    //       (Number(it.tax_per) || 0)) / 100
    //   ).toFixed(2),
    //   tax_acc_id: selectedTaxAcc?.acc_id || 0,
    //   tax_acc_name: selectedTaxAcc?.acc_name || "",
    //   qty_display: `${it.quantity} ${it.unit_symbol || ""}`,
    //   unit_name: it.unit_name || "",
    //   unit_symbol: it.unit_symbol || "",
    //   base_unit_name: it.base_unit_name || null,
    //   per_unit_id: Number(it.unit_id) || 0,   // ✅ always integer
    //   per_unit_symbol: it.unit_symbol || "",
    //   conversion: Number(it.conversion) || 1,
    //   per_conversion: Number(it.conversion) || 1,
    //   retail_qty: Number(it.retail_qty) || 0,
    //   is_serial: it.is_serial || "no",
    //   serials: it.serials || [],
    //   units: it.units || [],
    // })),

    customer: {
      acc_id: customerInfo?.acc_id,
      acc_name: customerInfo?.acc_name,
      institution_name: customerInfo?.institution_name,
      address: customerInfo?.address,
      contact_no: customerInfo?.contact_no,
    },

    masterData: {
      service_voucher_no: voucherNumber,
      is_serial: "no", // ✅ required
      is_cal_type: taxMode === "on_total" ? "on_total" : "individual",
      created_date: entryDate
        ? formatToUtc(finalEntryDate)
        : null,
      narration: narration || footerValues.description || "",
      sub_total: footerValues.subtotal,
      total_transport_cost: footerValues.total_transport_cost || 0,
      total_amount: footerValues.grandTotal,
      paid_amount: footerValues.paidAmount || 0,
      due_amount: footerValues.dueAmount || 0,
      services_acc_id: selectedServiceAcc?.acc_id || 0, // ✅ added
      discount_acc_id: selectedDiscountAcc?.acc_id || 0,
      tax_acc_id: selectedTaxAcc?.acc_id || 0,
      transport_acc_id: selectedTransportAcc?.acc_id || 0,
      conversion_rate,
      conversion_currency,
      // If taxMode is individual → all totals must be 0
      ...(taxMode === "on_total"
        ? {
          total_discount_per: footerValues.discountPct,
          total_discount: footerValues.total_discount,
          total_discount_mode: footerValues?.discountMode === "amount" ? "amount" : "percent",
          total_tax_per: footerValues.taxPct || authInfo?.item_tax || 0,
          total_tax: footerValues.totalTax,
        }
        : {
          total_discount_per: 0,
          total_discount: parseFloat(footerValues.total_discount || 0),
          total_discount_mode: "percent",
          total_tax_per: 0,
          total_tax: parseFloat(footerValues.totalTax || 0),
        }),

      ...(tag_field_id && tag_field_id.length > 0
        ? {
          tag_field_id: tag_field_id,
        }
        : {}),
    },

    orderId: footerValues.order_id || 0,

    payCart: (payServiceCart || []).map((p) => ({
      to_acc_id: p.from_acc_id || 0,
      to_acc_name: p.from_acc_name || "",
      tran_amount: String(p.tran_amount || 0),
    })),

    // tag_field_id: tagFieldId,
    // conversion_rate,
    // conversion_currency,
    // other_service_cost: 0,
  };

  // if (taxMode != "on_total") {
  //   payload.masterData.total_discount_per = 0;
  //   payload.masterData.total_discount = 0;
  //   payload.masterData.total_tax_per = 0;
  //   payload.masterData.total_tax = 0;
  // }

  return payload;
};
